import apiClient from '@/lib/api/apiClient'
import {
  getAuthTokenFromStorage,
  getStorageItem,
  setStorageItem,
  removeStorageItem,
  STORAGE_KEYS,
} from '@/lib/utils/storage'
import { formatApiError, showWarning } from '@/lib/utils/notifications'

/**
 * File Token Service
 * Manages file access tokens with automatic refresh mechanism
 */
class FileTokenService {
  private refreshInterval: ReturnType<typeof setInterval> | null = null

  /** Interval before next refresh — derived from API `expires_in` when available. */
  private getRefreshIntervalMs(expiresIn?: number): number {
    if (!expiresIn || expiresIn <= 0) return 5 * 60 * 1000
    // Renew shortly before expiry (Angular only refreshes on login; we keep token warm for previews).
    return Math.max((expiresIn - 5) * 1000, 10_000)
  }

  /**
   * Refresh file access token from backend
   * @returns Promise with token response or null on error
   */
  async refreshFileToken(): Promise<{ file_access_token: string; expires_in: number } | null> {
    const accessToken = getAuthTokenFromStorage()
    if (!accessToken) {
      console.warn('No access token found, cannot refresh file token')
      return null
    }

    try {
      // Use relative path to go through Vite proxy in development (avoids CORS)
      // In production, apiClient baseURL will be set to env.apiBaseUrl
      const response = await apiClient.get<{ file_access_token: string; expires_in: number }>(
        '/refresh-file-token',
        {
          headers: { token: accessToken, Token: accessToken },
        }
      )

      const token = response.data.file_access_token
      if (token) {
        setStorageItem(STORAGE_KEYS.fileAccessToken, token)
      }
      return response.data
    } catch (err) {
      showWarning(formatApiError(err))
      return null
    }
  }

  /**
   * Start automatic token refresh.
   * Refreshes token immediately (awaited so token is ready after load/refresh) then every 5 minutes.
   */
  startAutoRefresh() {
    this.stopAutoRefresh()
    void this.refreshFileToken().then((result) => {
      const intervalMs = this.getRefreshIntervalMs(result?.expires_in)
      this.refreshInterval = setInterval(() => {
        void this.refreshFileToken()
      }, intervalMs)
    })
  }

  /**
   * Stop automatic token refresh
   */
  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  }

  /**
   * Ensure we have a valid file access token (refresh if needed).
   * Retries once after a short delay if the first attempt fails (e.g. race after page refresh).
   * @returns The current or newly refreshed file access token, or null if refresh failed
   */
  async ensureFileToken(): Promise<string | null> {
    let result = await this.refreshFileToken()
    if (result?.file_access_token) return result.file_access_token
    const existing = this.getFileAccessToken()
    if (existing?.trim()) return existing
    // Retry once after brief delay (helps when auth token wasn't ready right after refresh)
    await new Promise((r) => setTimeout(r, 400))
    result = await this.refreshFileToken()
    if (result?.file_access_token) return result.file_access_token
    return this.getFileAccessToken()
  }

  /**
   * Get current file access token
   * @returns Current file access token or null
   */
  getFileAccessToken(): string | null {
    return getStorageItem(STORAGE_KEYS.fileAccessToken)
  }

  /**
   * Clear file access token
   */
  clearFileAccessToken() {
    removeStorageItem(STORAGE_KEYS.fileAccessToken)
  }
}

// Export singleton instance
export const fileTokenService = new FileTokenService()

